import { useEffect, useState } from 'react'
import Tooltip from '../Tooltip'
import {
  FEE_SLIDER,
  KRAKEN_FEES_CHECKED_NOTE,
  KRAKEN_FEE_PRESETS,
  MAX_FEE_PCT_PER_SIDE,
  formatFeePct,
  isValidFee,
  roundFee,
} from '../../config/fees'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../../config/glossary'

/**
 * The fee input: a slider over the usual venue range, a number box up to the
 * 5% ceiling, and Kraken's rates as presets. Controlled: the page owns the fee.
 * The Fee Calculator and a creator's record (/c/<handle>) both render it.
 */
const FeeInput = ({ value, onChange }: { value: number; onChange: (fee: number) => void }) => {
  const [text, setText] = useState(String(value))
  // A slider move or a preset rewrites the box; typing keeps what was typed.
  useEffect(() => {
    if (Number(text) !== value) setText(String(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  const typed = Number(text)
  const invalid = text.trim() === '' || !isValidFee(typed)
  return (
    <section className="rounded-lg border border-terminal-border bg-terminal-surface p-4" data-testid="fee-input">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label htmlFor="fee-slider-input" className="text-sm font-semibold text-gray-100">
          <Tooltip content={GLOSSARY.feeSlider}>Your fee per side</Tooltip>
        </label>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={MAX_FEE_PCT_PER_SIDE}
            step={0.01}
            value={text}
            onChange={(event) => {
              setText(event.target.value)
              const next = Number(event.target.value)
              if (event.target.value.trim() !== '' && isValidFee(next)) onChange(roundFee(next))
            }}
            aria-label="Fee per side, percent"
            aria-invalid={invalid}
            className="w-24 rounded-md border border-terminal-border bg-terminal-bg px-2 py-1 text-right font-mono text-sm tabular-nums text-gray-100"
            data-testid="fee-number"
          />
          <span className="text-sm text-gray-400">% per side</span>
        </div>
      </div>
      <input
        id="fee-slider-input"
        type="range"
        min={FEE_SLIDER.min}
        max={Math.max(FEE_SLIDER.max, value)}
        step={FEE_SLIDER.step}
        value={value}
        onChange={(event) => onChange(roundFee(Number(event.target.value)))}
        aria-valuetext={`${formatFeePct(value)} per side`}
        className="mt-3 w-full accent-sky-500"
        data-testid="fee-slider"
      />
      {invalid && (
        <p className="mt-1 text-xs text-red-300" role="alert" data-testid="fee-invalid">
          Enter a fee from 0% to {MAX_FEE_PCT_PER_SIDE}% per side.
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Fee presets" data-testid="fee-presets">
        {KRAKEN_FEE_PRESETS.map((preset) => {
          const on = Math.abs(value - preset.fee) < 1e-9
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => onChange(preset.fee)}
              aria-pressed={on}
              className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                on
                  ? 'border-sky-500/60 bg-sky-500/15 text-sky-200'
                  : 'border-terminal-border text-gray-300 hover:border-sky-500/50 hover:text-gray-100'
              }`}
            >
              {preset.label} <span className="font-mono tabular-nums">{formatFeePct(preset.fee)}</span>
              {preset.note && ` ${preset.note}`}
            </button>
          )
        })}
      </div>
      <p className="mt-1.5 text-xs text-gray-500" data-testid="fee-presets-checked">
        {KRAKEN_FEES_CHECKED_NOTE}
      </p>
      <p className="mt-2 text-xs text-gray-400" data-testid="fee-basis">
        Fee only, slippage not included: your fee is charged on entry and again on exit.
      </p>
    </section>
  )
}

export default FeeInput
